import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Button, HStack, Image, Link, Spinner, Text, VStack } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../types/bonus';
import { isIOSDevice } from '../../../../../utils/browser';
import SpotlightLeftIcons from '../../../../celebrations/assets/spotlight-left-icons.svg';
import SpotlightRightIcons from '../../../../celebrations/assets/spotlight-right-icons.svg';
import { GenericAttachment } from '../../types/generic-attachment';

type EmployeeSpotlightButtonProps = {
	attachment: GenericAttachment;
	bonus: Bonus;
	receiverFirstName: string;
};

export const EmployeeSpotlightButton: FC<EmployeeSpotlightButtonProps> = ({ attachment, bonus, receiverFirstName }) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'bonus_card' });
	const { track } = useAnalytics();
	const currentUser = useCurrentUser();

	// Show loading state when spotlight is being generated
	// Check both generation_in_progress (job started) and pending status (job queued but not started)
	const isGenerating = attachment.generation_in_progress || attachment.status === 'pending' || attachment.status === 'processing';

	if (isGenerating) {
		return (
			<VStack data-testid="employee-spotlight-button-loading">
				<Text fontSize={100} fontWeight="semibold" color="text.default">
					{t('spotlight.heading')}
				</Text>
				<HStack align="center">
					<Image src={SpotlightLeftIcons} alt="" aria-hidden display={{ base: 'none', sm: 'block' }} />
					<Button colorScheme="secondary" variant="solid" size="md" isDisabled leftIcon={<Spinner size="sm" />}>
						{t('spotlight.generating')}
					</Button>
					<Image src={SpotlightRightIcons} alt="" aria-hidden display={{ base: 'none', sm: 'block' }} />
				</HStack>
			</VStack>
		);
	}

	if (!attachment.url || !attachment.image_url) {
		return null;
	}

	const linkTarget = isIOSDevice() ? undefined : '_blank';

	return (
		<VStack>
			<Text fontSize={100} fontWeight="semibold" color="text.default">
				{t('spotlight.heading')}
			</Text>
			<HStack align="center">
				<Image src={SpotlightLeftIcons} alt="" aria-hidden display={{ base: 'none', sm: 'block' }} />
				<Button
					colorScheme="secondary"
					variant="solid"
					size="md"
					as={Link}
					href={attachment.url}
					target={linkTarget}
					_hover={{ textDecoration: 'none' }}
					onClick={() =>
						track(AnalyticsEvent.Recognition.SpotlightClicked, {
							spotlightId: attachment.employee_spotlight_id || attachment.id,
							viewerIsAdmin: currentUser.admin ? 'true' : 'false',
							viewerIsManager: currentUser.is_manager ? 'true' : 'false',
							parentId: bonus.id,
							parentType: bonus.parent_bonus_id ? 'Comment' : 'Bonus',
						})
					}
				>
					{t('spotlight.button', { name: receiverFirstName })}
				</Button>
				<Image src={SpotlightRightIcons} alt="" aria-hidden display={{ base: 'none', sm: 'block' }} />
			</HStack>
		</VStack>
	);
};
